import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MetricCard from '@/components/MetricCard';
import PageHeader from '@/components/PageHeader';
import StatusChip from '@/components/StatusChip';
import { listWarehouseRecords, type WarehouseRecordItem } from '@/services/adminApi';
import { getLocationTree, type ApiLocationNode } from '@/services/locationApi';
import { listVariboxes, mapApiVariboxToView, type VariboxViewRecord } from '@/services/variboxApi';
import { useI18n } from '@/i18n';

function countActiveLocations(nodes: ApiLocationNode[]): number {
  return nodes.reduce((acc, node) => {
    const self = node.active ? 1 : 0;
    return acc + self + countActiveLocations(node.children);
  }, 0);
}

export default function DashboardPage() {
  const { t, language } = useI18n();
  const navigate = useNavigate();
  const [allVariboxes, setAllVariboxes] = useState<VariboxViewRecord[]>([]);
  const [recentOperations, setRecentOperations] = useState<WarehouseRecordItem[]>([]);
  const [activeLocationsCount, setActiveLocationsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const metricLabels = [
    t('dashboard.totalAssets'),
    t('dashboard.availableAssets'),
    t('dashboard.assetsInRepair'),
    t('dashboard.assetsOut'),
  ];
  const metricHelpers = [
    t('dashboard.metricHelpers.totalAssets'),
    t('dashboard.metricHelpers.availableAssets'),
    t('dashboard.metricHelpers.assetsInRepair'),
    t('dashboard.metricHelpers.assetsOut'),
  ];
  const quickActionLabels = [
    t('dashboard.quickActionLabels.registerAssetBatch'),
    t('dashboard.quickActionLabels.scheduleInventoryRun'),
    t('dashboard.quickActionLabels.reviewExceptionReport'),
    t('dashboard.quickActionLabels.inviteUser'),
  ];
  const quickActionDescriptions = [
    t('dashboard.quickActionDescriptions.registerAssetBatch'),
    t('dashboard.quickActionDescriptions.scheduleInventoryRun'),
    t('dashboard.quickActionDescriptions.reviewExceptionReport'),
    t('dashboard.quickActionDescriptions.inviteUser'),
  ];

  const dashboardMetrics = useMemo(() => {
    const totalAssets = allVariboxes.length;
    const availableAssets = allVariboxes.filter((item) => item.status === 'available').length;
    const assetsInRepair = allVariboxes.filter((item) => item.status === 'repair' || item.status === 'damaged').length;
    const assetsOut = allVariboxes.filter((item) => item.status === 'occupied').length;

    const totalLocationsHelper = language === 'pl'
      ? `Aktywne lokalizacje: ${activeLocationsCount}`
      : `Active locations: ${activeLocationsCount}`;

    return [
      {
        label: metricLabels[0],
        value: String(totalAssets),
        delta: String(totalAssets),
        trend: 'up' as const,
        helper: totalLocationsHelper,
      },
      {
        label: metricLabels[1],
        value: String(availableAssets),
        delta: String(availableAssets),
        trend: availableAssets > 0 ? 'up' as const : 'flat' as const,
        helper: metricHelpers[1],
      },
      {
        label: metricLabels[2],
        value: String(assetsInRepair),
        delta: String(assetsInRepair),
        trend: assetsInRepair > 0 ? 'down' as const : 'up' as const,
        helper: metricHelpers[2],
      },
      {
        label: metricLabels[3],
        value: String(assetsOut),
        delta: String(assetsOut),
        trend: assetsOut > 0 ? 'up' as const : 'flat' as const,
        helper: metricHelpers[3],
      },
    ];
  }, [activeLocationsCount, allVariboxes, language, metricHelpers, metricLabels]);

  const toneByAction = (action: string) => {
    if (action.includes('delete') || action.includes('remove')) {
      return 'error';
    }

    if (action.includes('update') || action.includes('move')) {
      return 'info';
    }

    return 'success';
  };

  const recentMovements = recentOperations.slice(0, 4);

  useEffect(() => {
    let cancelled = false;

    const loadDashboard = async () => {
      setLoading(true);
      setErrorMessage('');

      try {
        const [variboxesResponse, locationsResponse, operationsResponse] = await Promise.all([
          listVariboxes({ limit: 200, offset: 0 }),
          getLocationTree(),
          listWarehouseRecords({ limit: 20, offset: 0 }),
        ]);

        if (cancelled) {
          return;
        }

        setAllVariboxes(variboxesResponse.items.map(mapApiVariboxToView));
        setActiveLocationsCount(countActiveLocations(locationsResponse.items));
        setRecentOperations(operationsResponse.items);
      } catch {
        if (!cancelled) {
          setErrorMessage(t('dashboard.apiError'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadDashboard();

    return () => {
      cancelled = true;
    };
  }, [t]);

  const handleQuickAction = (index: number) => {
    if (index === 0) {
      navigate('/assets?create=1');
      return;
    }

    if (index === 1) {
      navigate('/inventory');
      return;
    }

    if (index === 2) {
      navigate('/reports');
      return;
    }

    navigate('/users');
  };

  return (
    <Stack spacing={3} sx={{ width: '100%', minWidth: 0 }}>
      <PageHeader
        eyebrow={t('dashboard.eyebrow')}
        title={t('dashboard.title')}
        description={t('dashboard.description')}
        action={
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            <Chip label={t('dashboard.lastSync')} color="primary" variant="outlined" />
            <Button variant="contained" onClick={() => navigate('/assets')}>{t('common.buttons.openAssets')}</Button>
          </Stack>
        }
      />

      <Grid container spacing={3} sx={{ width: '100%', minWidth: 0, maxWidth: '100%' }}>
        {dashboardMetrics.map((metric, index) => (
          <Grid item xs={12} sm={6} xl={3} key={`${metric.label}-${index}`} sx={{ minWidth: 0 }}>
            <MetricCard
              {...metric}
              label={metricLabels[index]}
              helper={metric.helper}
              iconLabel={['AS', 'LC', 'RP', 'US'][index]}
            />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3} sx={{ width: '100%', minWidth: 0, maxWidth: '100%' }}>
        <Grid item xs={12} xl={7} sx={{ minWidth: 0 }}>
          <Card sx={{ height: '100%', minWidth: 0, width: '100%' }}>
            <CardContent>
              {errorMessage ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {errorMessage}
                </Alert>
              ) : null}

              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="h6">{t('dashboard.recentMovements')}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('dashboard.recentMovementsDescription')}
                  </Typography>
                </Box>
                <Chip label={t('common.status.liveFeed')} color="success" size="small" />
              </Stack>

              <List disablePadding>
                {loading ? (
                  <ListItem disablePadding sx={{ py: 1.5 }}>
                    <ListItemText primary={t('dashboard.loadingData')} primaryTypographyProps={{ fontWeight: 700 }} />
                  </ListItem>
                ) : null}
                {recentMovements.map((movement, index) => (
                  <ListItem
                    key={movement.id}
                    disablePadding
                    sx={{
                      py: 1.5,
                      borderTop: index === 0 ? 'none' : '1px solid rgba(148,163,184,0.16)',
                    }}
                  >
                    <ListItemText
                      primary={movement.target_label || movement.action}
                      secondary={`${movement.actor_name || movement.actor_email} · ${new Date(movement.created_at).toLocaleTimeString(language === 'pl' ? 'pl-PL' : 'en-GB', { hour: '2-digit', minute: '2-digit' })}`}
                      primaryTypographyProps={{ fontWeight: 700 }}
                    />
                    <Stack spacing={1} alignItems="flex-end">
                      <StatusChip label={movement.action} tone={toneByAction(movement.action.toLowerCase())} />
                      <Typography variant="caption" color="text.secondary">
                        {movement.target_type}
                      </Typography>
                    </Stack>
                  </ListItem>
                ))}
                {!loading && recentMovements.length === 0 ? (
                  <ListItem disablePadding sx={{ py: 1.5 }}>
                    <ListItemText
                      primary={t('reports.noReports')}
                      secondary={t('dashboard.recentMovementsDescription')}
                      primaryTypographyProps={{ fontWeight: 700 }}
                    />
                  </ListItem>
                ) : null}
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} xl={5} sx={{ minWidth: 0 }}>
          <Stack spacing={3} sx={{ height: '100%', minWidth: 0 }}>
            <Card sx={{ minWidth: 0, width: '100%' }}>
              <CardContent>
                <Stack spacing={2}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="h6">{t('dashboard.quickActions')}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('dashboard.quickActionsDescription')}
                      </Typography>
                    </Box>
                    <Chip label={t('common.status.operationalActions')} size="small" variant="outlined" />
                  </Stack>

                  <Grid container spacing={1.5}>
                    {quickActionLabels.map((label, index) => (
                      <Grid item xs={12} sm={6} key={label}>
                        <Button
                          fullWidth
                          variant="outlined"
                          onClick={() => handleQuickAction(index)}
                          sx={{
                            height: '100%',
                            alignItems: 'flex-start',
                            textAlign: 'left',
                            justifyContent: 'flex-start',
                            py: 1.5,
                            gap: 1.5,
                          }}
                        >
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: '999px',
                              display: 'grid',
                              placeItems: 'center',
                              bgcolor: 'rgba(20, 63, 107, 0.12)',
                              color: 'primary.main',
                              fontWeight: 800,
                              flexShrink: 0,
                            }}
                          >
                            {['RA', 'SC', 'RE', 'IN'][index]}
                          </Box>
                          <Stack spacing={0.5} alignItems="flex-start">
                            <Typography variant="subtitle2">{label}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'normal' }}>
                              {quickActionDescriptions[index]}
                            </Typography>
                          </Stack>
                        </Button>
                      </Grid>
                    ))}
                  </Grid>
                </Stack>
              </CardContent>
            </Card>

            <Card sx={{ minWidth: 0, width: '100%' }}>
              <CardContent>
                <Stack spacing={2}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="h6">{t('dashboard.loadingPreview')}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('dashboard.loadingPreviewDescription')}
                      </Typography>
                    </Box>
                    <Chip label={t('common.status.productionPreview')} size="small" color="info" variant="outlined" />
                  </Stack>

                  <LinearProgress variant="determinate" value={68} sx={{ height: 10, borderRadius: 999 }} />

                  <Stack spacing={1.5}>
                    <Skeleton variant="rounded" height={64} />
                    <Skeleton variant="rounded" height={64} />
                    <Skeleton variant="rounded" height={64} />
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
}